import { useCallback, useEffect, useState } from "react";
import EmailDeliveryState from "./EmailDeliveryState";
import { Explain } from "@/components/area/Explain";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FollowupEditor } from "./FollowupEditor";
import { FollowupList } from "./FollowupList";
import { AutomationTest } from "./AutomationTest";

const RES = { email_queued: "email in coda", task_created: "attività creata", skipped: "saltato", running: "in corso", error: "errore", enrolled: "inserito nella sequenza", stopped: "sequenza fermata" };

// Ready-made automations: the owner switches them on/off. Emails only enter the outbox (real sending is off).
export default function CrmAutomations({ tid }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/t/${tid}/automations`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [api, tid]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (a, v) => {
    setBusy(a.key);
    try { await api.put(`/t/${tid}/automations/${a.key}`, { enabled: v }); toast.success(v ? "Automazione attivata" : "Automazione spenta"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(""); load();
  };
  const runNow = async () => {
    setBusy("run");
    try { const { data } = await api.post(`/t/${tid}/automations/run`); const n = Object.values(data.handled).filter((x) => typeof x === "number").reduce((s, x) => s + x, 0); toast.success(n ? `${n} ${n === 1 ? "evento gestito" : "eventi gestiti"}` : "Niente di nuovo da gestire"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(""); load();
  };
  if (!d) return <Loader2 className="animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" data-testid="crm-automations">
      <EmailDeliveryState d={d.email_delivery} testid="automations-email-state" />
      <Explain testid="automations-explain">Le automazioni valgono solo per ciò che succede dopo l'attivazione; i dati di prova vengono saltati. Ogni elemento è elaborato una volta sola, con al massimo 3 tentativi.</Explain>
      {d.automations.map((a) => (
        <section key={a.key} className="space-y-2 rounded-lg border border-slate-200 bg-white p-4" data-testid={`automation-${a.key}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0"><p className="text-sm font-medium text-slate-800">{a.title}</p><p className="text-xs text-slate-600">{a.description}</p></div>
            <Switch checked={a.enabled} disabled={!d.can_manage || busy === a.key} onCheckedChange={(v) => toggle(a, v)} aria-label={a.title} data-testid={`automation-toggle-${a.key}`} />
          </div>
          <p className="text-xs text-slate-500" data-testid={`automation-status-${a.key}`}>{a.enabled ? `Attiva dal ${formatDate(a.enabled_at, true)}` : "Spenta"} · ultimi 30 giorni: {a.count_30d}</p>
          {a.recent.length > 0 && <ul className="space-y-0.5 text-xs text-slate-500">{a.recent.map((r, i) => <li key={i}>{formatDate(r.at, true)} · {RES[r.result] || r.result}{r.detail && ["skipped", "error"].includes(r.result) ? ` (${r.detail})` : ""}</li>)}</ul>}
          {d.can_manage && <AutomationTest tid={tid} akey={a.key} />}
          {a.key === "no_reply_followup" && <>
            <FollowupEditor key={a.config_rev || 0} tid={tid} config={a.config} canManage={d.can_manage} onSaved={load} />
            <div className="border-t border-slate-100 pt-2"><p className="text-xs font-medium text-slate-700">Contatti nella sequenza</p><FollowupList tid={tid} canWrite={d.can_write} reload={d} /></div></>}
        </section>))}
      {d.can_manage ? <Button variant="outline" className="h-10" disabled={!!busy || !d.automations.some((a) => a.enabled)} onClick={runNow} data-testid="automations-run">{busy === "run" && <Loader2 size={15} className="mr-1.5 animate-spin" />}Esegui ora</Button>
        : <p className="text-xs text-slate-500" data-testid="automations-readonly">Il tuo ruolo non può accendere o spegnere le automazioni (lo decide il titolare).</p>}
    </div>);
}
